import angular from 'angular';
import moment from 'moment';
import {
    IMovimentacao,
    IMovimentacaoHistoricoResumo,
    IMovimentacaoUpdatePayload
} from '../../models/movimentacoes.service.model';
import { IMovimentacaoPeriodo } from '../../models/movimentacoes-periodos.service.model';
import { IMovimentacaoWorkflowAcao } from '../../models/movimentacoes-workflow.service.model';
import { IMovimentacaoFormModel } from '../components/movimentacao-form/movimentacoes-movimentacao-form.model';
import {
    IMovimentacaoDetalheActionTexts,
    IMovimentacaoDrawerPermissionContext,
    IMovimentacaoDrawerSummaryViewModel,
    IMovimentacaoHistoricoItemView,
    TMovimentacaoDrawerAction
} from '../components/detalhe-drawer/movimentacoes-detalhe-drawer.model';
import {
    isMovimentacaoEventoDias,
    isMovimentacaoEventoHoras,
    formatMovimentacaoReferenciaLabel,
    formatMovimentacaoValorLabel,
    formatMovimentacaoVigenciaLabel,
    formatMovimentacaoMonthNumber,
    normalizeMovimentacaoValor,
    parseMovimentacaoHorasToMinutes,
    resolveMovimentacaoEntityIdentifier
} from '../../helpers/movimentacoes-value.helpers';
import {
    isMovimentacaoDataPagamentoEnabled,
    normalizeMovimentacaoDate
} from './movimentacoes-form.helpers';

/**
 * Converte o histórico bruto da API para o view model do drawer.
 */
export function buildMovimentacaoHistoricoItems(items: IMovimentacaoHistoricoResumo[]): IMovimentacaoHistoricoItemView[] {
    return (items || []).map((item) => ({
        id: item.movimentacaohistorico,
        titulo: getMovimentacaoHistoricoTitulo(item.tipo),
        dataHora: formatMovimentacaoDateTime(item.created_at),
        responsavel: item.created_by?.nome || '',
        descricao: item.motivo || '',
        indicatorClass: getMovimentacaoHistoricoIndicatorClass(item.tipo)
    }));
}

/**
 * Formata data e hora de histórico para leitura em UI.
 * O created_at vem da API sem fuso e em UTC; converte para o horário local antes de exibir.
 */
export function formatMovimentacaoDateTime(value: string | null | undefined): string {
    if (!value) {
        return '-';
    }

    const parsed = moment.utc(value);
    return parsed.isValid() ? parsed.local().format('DD/MM/YYYY [às] HH:mm') : '-';
}

/**
 * Resolve o título exibido para cada tipo de histórico.
 */
export function getMovimentacaoHistoricoTitulo(tipo: number): string {
    const titles: Record<number, string> = {
        0: 'Movimentação criada',
        1: 'Movimentação alterada',
        2: 'Movimentação enviada',
        3: 'Movimentação efetuada',
        4: 'Movimentação excluída',
        5: 'Movimentação cancelada',
        6: 'Movimentação recusada',
        7: 'Movimentação aprovada',
        8: 'Estorno solicitado',
        9: 'Estorno confirmado',
        10: 'Estorno recusado'
    };

    return titles[tipo] || `Evento ${tipo}`;
}

/**
 * Resolve a classe visual do indicador de cada item do histórico.
 */
export function getMovimentacaoHistoricoIndicatorClass(tipo: number): string {
    const indicators: Record<number, string> = {
        0: 'indicator--info',
        1: 'indicator--info',
        2: 'indicator--info',
        3: 'indicator--success',
        4: 'indicator--warning',
        5: 'indicator--danger',
        6: 'indicator--danger',
        7: 'indicator--success',
        8: 'indicator--warning',
        9: 'indicator--success',
        10: 'indicator--danger'
    };

    return indicators[tipo] || 'indicator--info';
}

/**
 * Adapta o detalhe carregado para o model usado pelo formulário em modo de edição.
 */
export function buildMovimentacaoEditModel(detail: IMovimentacao): IMovimentacaoFormModel {
    const evento: IMovimentacaoFormModel['evento'] & { codigo_nome?: string } = angular.copy(detail.evento || {});
    const rawValor = detail.valor;

    if (evento.codigo && evento.nome) {
        evento.codigo_nome = `${evento.codigo} - ${evento.nome}`;
    }

    let normalizedValor: string | number | null = rawValor;
    if (isMovimentacaoEventoHoras(evento)) {
        normalizedValor = parseMovimentacaoHorasToMinutes(rawValor);
    } else if (isMovimentacaoEventoDias(evento) &&
        rawValor !== null &&
        typeof rawValor !== 'undefined' &&
        `${rawValor}`.trim() !== '') {
        const parsedValor = Number(`${rawValor}`.replace(',', '.'));
        normalizedValor = Number.isFinite(parsedValor) ? parsedValor : rawValor;
    }

    return angular.copy({
        trabalhador: detail.trabalhador,
        evento,
        lotacao: detail.lotacao,
        valor: normalizedValor,
        referencia: detail.referencia || '',
        datapagamento: detail.datapagamento,
        vigencia: Number(detail.vigencia) as 1 | 2 | 3,
        vigenciamesfinal: detail.vigenciamesfinal || null,
        vigenciaanofinal: detail.vigenciaanofinal || null
    });
}

/**
 * Monta o payload de atualização a partir do model editado no drawer.
 */
export function buildMovimentacaoUpdatePayload(model: IMovimentacaoFormModel, versao: number): IMovimentacaoUpdatePayload {
    const raw = model.valor;
    const normalizedValor = normalizeMovimentacaoValor(raw, model.evento);

    return {
        evento: resolveMovimentacaoEntityIdentifier(model.evento, 'evento', 'codigo'),
        lotacao: resolveMovimentacaoEntityIdentifier(model.lotacao, 'lotacao', 'codigo'),
        valor: normalizedValor === null ? `${raw || ''}` : normalizedValor,
        referencia: model.referencia ? `${model.referencia}`.trim() : null,
        datapagamento: normalizeMovimentacaoDate(model.datapagamento),
        vigencia: Number(model.vigencia),
        vigenciamesfinal: Number(model.vigencia) === 3 ? model.vigenciamesfinal : null,
        vigenciaanofinal: Number(model.vigencia) === 3 ? model.vigenciaanofinal : null,
        versao
    };
}

/**
 * Resolve os textos de confirmação usados nas ações críticas do drawer.
 */
export function buildMovimentacaoDetalheActionTexts(action: TMovimentacaoDrawerAction): IMovimentacaoDetalheActionTexts {
    if (action === 'delete') {
        return {
            title: 'Excluir',
            confirmText: 'Deseja excluir esta movimentação?',
            closeButton: 'Cancelar',
            confirmButton: 'Sim, excluir'
        };
    }

    if (action === 'send') {
        return {
            title: 'Enviar',
            confirmText: 'Deseja enviar esta movimentação?',
            closeButton: 'Voltar',
            confirmButton: 'Sim, enviar'
        };
    }

    return {
        title: 'Cancelar',
        confirmText: 'Deseja cancelar esta movimentação enviada?',
        closeButton: 'Voltar',
        confirmButton: 'Cancelar movimentação'
    };
}

/**
 * Resolve a mensagem de sucesso exibida após cada ação do drawer.
 */
export function getMovimentacaoDrawerSuccessMessage(action: TMovimentacaoDrawerAction): string {
    const messages: Record<TMovimentacaoDrawerAction, string> = {
        delete: 'Movimentação excluída com sucesso.',
        send: 'Movimentação enviada com sucesso.',
        cancel: 'Movimentação cancelada com sucesso.'
    };

    return messages[action];
}

/**
 * Resolve a mensagem padrão de erro para cada ação do drawer.
 */
export function getMovimentacaoDrawerErrorMessage(action: TMovimentacaoDrawerAction): string {
    const messages: Record<TMovimentacaoDrawerAction, string> = {
        delete: 'Não foi possível excluir a movimentação.',
        send: 'Não foi possível enviar a movimentação.',
        cancel: 'Não foi possível cancelar a movimentação.'
    };

    return messages[action];
}

/**
 * Monta o subtítulo do drawer com tipo da movimentação e período de contexto.
 */
export function getMovimentacaoDetalheSubtitle(
    detail: IMovimentacao | null,
    selectedPeriod: IMovimentacaoPeriodo | null
): string {
    if (!detail) {
        return '';
    }

    const tipoValor = Number(detail.evento?.tipovalor);
    const periodo = selectedPeriod
        ? `${formatMovimentacaoMonthNumber(selectedPeriod.mes)}/${selectedPeriod.ano}`
        : `${formatMovimentacaoMonthNumber(detail.movimentacaoperiodo.mes)}/${detail.movimentacaoperiodo.ano}`;

    if (tipoValor === 0) {
        return `Rendimento - ${periodo}`;
    }

    if (tipoValor === 1) {
        return `Desconto - ${periodo}`;
    }

    return periodo;
}

/**
 * Monta o view model do resumo exibido no modo de leitura do drawer.
 */
export function buildMovimentacaoDrawerSummaryView(
    detail: IMovimentacao | null,
    $filter: angular.IFilterFunction,
    colaboradorFotoUrl?: string | null
): IMovimentacaoDrawerSummaryViewModel | null {
    if (!detail) {
        return null;
    }

    return {
        colaboradorNome: detail.trabalhador?.nome || '-',
        colaboradorMatricula: detail.trabalhador?.codigo || '-',
        colaboradorFotoUrl: colaboradorFotoUrl || undefined,
        valor: getMovimentacaoDrawerValorLabel(detail, $filter),
        dataPagamento: getMovimentacaoDrawerPagamentoLabel(detail),
        showDataPagamento: isMovimentacaoDataPagamentoEnabled(),
        lotacaoNome: detail.lotacao?.nome || '-',
        lotacaoCodigoLabel: getMovimentacaoDrawerLotacaoCodigoLabel(detail),
        referencia: getMovimentacaoDrawerReferenciaLabel(detail),
        vigenciaTitle: getMovimentacaoDrawerVigenciaTitle(detail),
        vigenciaDescription: getMovimentacaoDrawerVigenciaDescription(detail)
    };
}

/**
 * Formata o valor principal exibido no resumo do drawer.
 */
function getMovimentacaoDrawerValorLabel(
    detail: IMovimentacao | null,
    $filter: angular.IFilterFunction
): string {
    if (!detail) {
        return '-';
    }

    return formatMovimentacaoValorLabel(detail.valor, detail.evento, $filter);
}

/**
 * Formata a data de pagamento exibida no resumo do drawer.
 */
function getMovimentacaoDrawerPagamentoLabel(detail: IMovimentacao | null): string {
    const raw = detail?.datapagamento;

    if (!raw) {
        return '-';
    }

    const parsed = moment(raw, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601], true);

    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-';
}

/**
 * Formata a referência textual exibida no resumo do drawer.
 */
function getMovimentacaoDrawerReferenciaLabel(detail: IMovimentacao | null): string {
    return formatMovimentacaoReferenciaLabel(detail?.referencia);
}

/**
 * Resolve o texto secundário com o código da lotação no resumo do drawer.
 */
function getMovimentacaoDrawerLotacaoCodigoLabel(detail: IMovimentacao | null): string {
    const codigo = detail?.lotacao?.codigo ? `${detail.lotacao.codigo}`.trim() : '';
    return codigo ? `Código: ${codigo}` : '';
}

/**
 * Resolve o título principal da vigência exibida no resumo do drawer.
 */
function getMovimentacaoDrawerVigenciaTitle(detail: IMovimentacao | null): string {
    if (!detail) {
        return '-';
    }

    return formatMovimentacaoVigenciaLabel(detail, {
        unicaLabel: 'Apenas neste período',
        mensalLabel: 'Sempre (todo mês)',
        emptyLabel: '-',
        untilPrefix: 'Até'
    });
}

/**
 * Resolve a descrição auxiliar da vigência exibida no resumo do drawer.
 */
function getMovimentacaoDrawerVigenciaDescription(detail: IMovimentacao | null): string {
    if (!detail) {
        return '';
    }

    if (Number(detail.vigencia) === 1) {
        return 'Lançado apenas neste período.';
    }

    if (Number(detail.vigencia) === 2) {
        return 'Lançado todo mês a partir deste período.';
    }

    if (Number(detail.vigencia) === 3) {
        return 'Lançado todo mês a partir deste período até o período informado (inclusivo).';
    }

    return '';
}

/**
 * Define se o drawer pode entrar em modo de edição.
 */
export function canEditMovimentacaoDrawer(context: IMovimentacaoDrawerPermissionContext): boolean {
    return context.canManage &&
        !!context.detail &&
        Number(context.detail.situacao) === 0 &&
        !context.actionBusy &&
        !context.selectedPeriodClosed;
}

/**
 * Define se o drawer pode excluir a movimentação atual.
 */
export function canDeleteMovimentacaoDrawer(context: IMovimentacaoDrawerPermissionContext): boolean {
    return context.canManage &&
        !!context.detail &&
        Number(context.detail.situacao) === 0 &&
        !context.actionBusy &&
        !context.isEditMode &&
        !context.selectedPeriodClosed;
}

/**
 * Define se o drawer pode enviar a movimentação atual.
 */
export function canSendMovimentacaoDrawer(context: IMovimentacaoDrawerPermissionContext): boolean {
    return context.canManage &&
        !!context.detail &&
        Number(context.detail.situacao) === 0 &&
        !context.actionBusy &&
        !context.isEditMode &&
        !context.selectedPeriodClosed;
}

/**
 * Define se o drawer pode cancelar o envio da movimentação atual.
 * A regra de negócio (enviado, criador, fora de "Aguardando estorno", período aberto e no prazo)
 * vem pronta do backend em `_podecancelar`; aqui ficam só os controles de UI.
 */
export function canCancelMovimentacaoDrawer(context: IMovimentacaoDrawerPermissionContext): boolean {
    return context.canManage &&
        !!context.detail &&
        context.detail._podecancelar === true &&
        !context.actionBusy &&
        !context.isEditMode;
}

/**
 * Define se o drawer pode salvar a edição em andamento.
 */
export function canSaveMovimentacaoDrawerEdit(context: IMovimentacaoDrawerPermissionContext): boolean {
    return context.canManage &&
        context.isEditMode &&
        !context.actionBusy &&
        !context.selectedPeriodClosed &&
        !!context.editState.formRef &&
        !!context.editState.valid;
}

/**
 * Resolve a classe visual do botão de cada ação de workflow.
 */
export function getMovimentacaoWorkflowActionButtonClass(action: IMovimentacaoWorkflowAcao): string {
    const codigo = `${action.codigo || ''}`.toUpperCase();

    if (codigo === 'RECUSAR' || codigo === 'RECUSAR_ESTORNO') {
        return 'btn btn-tertiary danger';
    }

    if (codigo === 'APROVAR' || codigo === 'CONFIRMAR_ESTORNO') {
        return 'btn btn-primary';
    }

    return 'btn btn-default';
}

/**
 * Monta os textos de confirmação das ações de workflow no drawer.
 */
export function buildMovimentacaoWorkflowActionTexts(codigo: string): IMovimentacaoDetalheActionTexts {
    const normalizedCodigo = `${codigo || ''}`.toUpperCase();

    if (normalizedCodigo === 'APROVAR') {
        return {
            title: 'Aprovar',
            confirmText: 'Deseja aprovar esta movimentação?',
            closeButton: 'Voltar',
            confirmButton: 'Sim, aprovar'
        };
    }

    if (normalizedCodigo === 'SOLICITAR_ESTORNO') {
        return {
            title: 'Solicitar estorno',
            confirmText: 'Deseja solicitar o estorno desta movimentação?',
            closeButton: 'Voltar',
            confirmButton: 'Solicitar estorno'
        };
    }

    if (normalizedCodigo === 'CONFIRMAR_ESTORNO') {
        return {
            title: 'Confirmar estorno',
            confirmText: 'Deseja confirmar o estorno desta movimentação?',
            closeButton: 'Voltar',
            confirmButton: 'Confirmar estorno'
        };
    }

    if (normalizedCodigo === 'RECUSAR') {
        return {
            title: 'Recusar',
            confirmText: 'Deseja recusar esta movimentação?',
            closeButton: 'Voltar',
            confirmButton: 'Sim, recusar'
        };
    }

    return {
        title: 'Recusar estorno',
        confirmText: 'Deseja recusar o estorno desta movimentação?',
        closeButton: 'Voltar',
        confirmButton: 'Recusar estorno'
    };
}

/**
 * Resolve a mensagem de sucesso das ações de workflow no drawer.
 */
export function getMovimentacaoWorkflowActionSuccessMessage(codigo: string): string {
    const messages: Record<string, string> = {
        APROVAR: 'Movimentação aprovada com sucesso.',
        RECUSAR: 'Movimentação recusada com sucesso.',
        SOLICITAR_ESTORNO: 'Solicitação de estorno enviada com sucesso.',
        CONFIRMAR_ESTORNO: 'Estorno confirmado com sucesso.',
        RECUSAR_ESTORNO: 'Estorno recusado com sucesso.'
    };

    return messages[`${codigo || ''}`.toUpperCase()] || 'Ação de workflow executada com sucesso.';
}

/**
 * Resolve a mensagem padrão de erro das ações de workflow no drawer.
 */
export function getMovimentacaoWorkflowActionErrorMessage(codigo: string): string {
    const messages: Record<string, string> = {
        APROVAR: 'Não foi possível aprovar a movimentação.',
        RECUSAR: 'Não foi possível recusar a movimentação.',
        SOLICITAR_ESTORNO: 'Não foi possível solicitar o estorno da movimentação.',
        CONFIRMAR_ESTORNO: 'Não foi possível confirmar o estorno da movimentação.',
        RECUSAR_ESTORNO: 'Não foi possível recusar o estorno da movimentação.'
    };

    return messages[`${codigo || ''}`.toUpperCase()] || 'Não foi possível executar a ação de workflow.';
}

/**
 * Converte a ação de workflow da API para o formato esperado pelo modal legado de comentário.
 */
export function buildMovimentacaoWorkflowModalAction(action: IMovimentacaoWorkflowAcao): any {
    const texts = buildMovimentacaoWorkflowActionTexts(action.codigo);

    return {
        id: action.codigo,
        code: action.codigo,
        title: texts.title,
        comentario: '',
        requiredcomment: !!action.exigirmotivo,
        modal: action.exigirmotivo
            ? {
                comentario: {
                    cancelar: texts.closeButton,
                    confirmar: texts.confirmButton,
                    placeholder: 'Informe o motivo',
                    textos_acima: [texts.confirmText],
                    textos_abaixo: []
                }
            }
            : {
                confirmacao: {
                    cancelar: texts.closeButton,
                    confirmar: texts.confirmButton,
                    textos: [texts.confirmText]
                }
            }
    };
}
